import { ITEM_TYPES } from '../constants';
import {
    MS_PER_MONTH,
    daysBetween,
    debtBalanceAt,
    debtPaymentDue,
    debtTermsOf,
    itemMonthlyCashflow,
    monthlyAmount,
    monthsBetween,
    num,
    toDate,
} from './engine';
import { estimateItemValue, estimateLiquidAssets } from './review';

// F29 — "Since your last visit".
//
// On returning after an absence, say how much net worth moved while the user
// wasn't looking. It makes the passage of time visible and reframes what a
// manual-entry planner is: the plan keeps working whether or not you touch it.
//
// Relationship to F28 (load-bearing): these share arithmetic, not state. This
// module reads NO F28 field — no `valueAsOf`, no `liquidAssetsAsOf`, no
// estimate list. F28 asks "how old is this number?"; F29 asks "what happened
// in the last 23 days?".
//
// Contract, same as review.js: every function takes explicit dates and never
// calls `new Date()`.
//
// ---------------------------------------------------------------------------
// Two sides, and every transfer visible on both
// ---------------------------------------------------------------------------
//
//   change in net worth = ASSETS + CASH FLOW
//
//   ASSETS     what you own got more valuable, or you own more of it:
//              appreciation, investment growth, money you added, and the
//              principal you repaid — repaying a mortgage is how you come to
//              own more of your own flat.
//
//   CASH FLOW  the money that actually moved: income, spending, rent, the
//              whole debt instalment, dividends, and the contributions leaving
//              your account.
//
// The point of the split is that **moving money is not making money**. A
// contribution leaves cash and lands in a fund; a mortgage instalment leaves
// cash and part of it becomes equity. Both therefore appear twice, with
// opposite signs, and cancel out of the total — which is exactly what should
// happen, because a transfer creates nothing. What survives is real: growth on
// what you own, income you earned, money you spent, and interest you were
// charged.
//
// An earlier version tried to net this off in advance — debt service excluded
// from cash, principal excluded from assets, only interest shown. The totals
// were right, but it meant a row labelled "cash flow" hiding most of the cash
// that had actually flowed. Showing both legs is longer and truthful.
//
// Every level sums exactly to the one above it: parts to their item, items to
// their side, both sides to the headline.

// Below these, the banner is noise rather than news. Away is the honest one —
// three days is roughly when "since I last looked" starts to mean anything.
export const MIN_AWAY_DAYS = 3;
export const MIN_DELTA_PCT = 0.001;
export const MIN_DELTA_ABS = 100;

// Past this, counting in days stops helping and the copy switches to months.
export const MONTHS_COPY_THRESHOLD_DAYS = 60;

// estimateItemValue and estimateLiquidAssets both return null for a value they
// can't roll and for a non-positive window. Subtracting from that would put NaN
// in the headline, so nothing here trusts a return value without checking it.
const usable = (value) => typeof value === 'number' && isFinite(value);

const isAsset = (item) =>
    item?.type === ITEM_TYPES.REAL_ESTATE || item?.type === ITEM_TYPES.OTHER_INVESTMENT;

/**
 * What a debt did over the window: what was paid, what of that was interest,
 * and what therefore became equity.
 *
 * Interest is charged month by month on what was actually still owed, because
 * the balance shrinks as instalments land. An earlier version froze the balance
 * at the start of the window and multiplied once — harmless over days, but
 * 2 220 Kč too much over a year on the demo portfolio (1.8 % of the row), which
 * pulled the headline *down* by that much since interest is a cost.
 *
 * `debtBalanceAt` is the app's one source of truth for what is owed on a date,
 * and what every other page uses; this asks it once per month rather than once
 * for the whole window. Nothing is replayed here — that is its job.
 */
const debtOverWindow = (item, from, to, months) => {
    const terms = debtTermsOf(item);
    if (!terms) return null;

    const payment = debtPaymentDue(item, to) * months;
    const monthlyRate = num(terms.interestRate) / 12;

    const balanceAtMonth = (index) => {
        const balance = debtBalanceAt(item, new Date(from.getTime() + index * MS_PER_MONTH));
        return usable(balance) && balance > 0 ? balance : 0;
    };

    let interest = 0;
    if (monthlyRate > 0) {
        const wholeMonths = Math.floor(months);
        for (let i = 0; i < wholeMonths; i++) interest += balanceAtMonth(i) * monthlyRate;

        // A part-month is charged pro-rata on the balance standing at its start,
        // the same treatment every other row here gives a partial month.
        const remainder = months - wholeMonths;
        if (remainder > 0) interest += balanceAtMonth(wholeMonths) * monthlyRate * remainder;
    }

    return {
        payment,
        interest,
        // A settled debt pays nothing; negative amortization repays nothing.
        principal: Math.max(0, payment - interest),
    };
};

/** Contribution into an investment over the window, as a positive number. */
const contributionOverWindow = (item, months) =>
    monthlyAmount(item.monthlyContribution, item.contributionFrequency) * months;

/**
 * The asset side of one item: `[{ key, value }]`, or an empty list for items
 * that own nothing and owe nothing.
 */
const assetPartsOf = (item, from, to, months) => {
    const parts = [];
    const debt = debtOverWindow(item, from, to, months);

    if (isAsset(item)) {
        const estimated = estimateItemValue(item, from, to);
        const delta = usable(estimated) ? estimated - num(item.currentValue) : 0;

        if (item.type === ITEM_TYPES.OTHER_INVESTMENT) {
            // The roll already includes the contribution, so growth is what's
            // left once the money the user added is taken back out. Growth
            // therefore includes the return earned *on* the contribution, which
            // is the natural reading of "what did the fund do for me".
            const contribution = contributionOverWindow(item, months);
            parts.push({ key: 'growth', value: delta - contribution });
            parts.push({ key: 'contribution', value: contribution });
        } else {
            parts.push({ key: 'appreciation', value: delta });
        }
    }

    if (debt && debt.principal !== 0) {
        // Named for what it means to the user: a mortgage buys you more of your
        // own property, a loan simply shrinks.
        parts.push({
            key: item.type === ITEM_TYPES.REAL_ESTATE ? 'mortgageRepaid' : 'debtRepaid',
            value: debt.principal,
        });
    }

    return parts;
};

/**
 * The cash-flow side of one item. Decomposed by hand rather than read off
 * `itemMonthlyCashflow`, so each part can be labelled — but it must add up to
 * exactly what that function returns, which `awayDelta.test.js` asserts.
 */
const flowPartsOf = (item, to, months) => {
    const parts = [];
    const push = (key, value) => { if (value !== 0) parts.push({ key, value }); };

    switch (item.type) {
        case ITEM_TYPES.REAL_ESTATE:
            if (item.isRented) {
                push('rent', monthlyAmount(item.monthlyRentalIncome, item.amountFrequency) * months);
            } else {
                push('costs', -monthlyAmount(item.monthlyCosts, item.amountFrequency) * months);
            }
            if (item.hasMortgage) push('debtPayment', -debtPaymentDue(item, to) * months);
            break;

        case ITEM_TYPES.OTHER_INVESTMENT:
            // A dividend-paying fund hands cash back, so the contribution and
            // the payout partly offset — the cash line is the net of the two.
            if (item.isIncome) {
                push('dividend', (num(item.currentValue) * num(item.annualIncomeYield)) / 12 * months);
            }
            push('contribution', -contributionOverWindow(item, months));
            break;

        case ITEM_TYPES.INCOME:
            push('income', monthlyAmount(item.monthlyIncome, item.amountFrequency) * months);
            break;

        case ITEM_TYPES.EXPENSE:
            push('expense', -monthlyAmount(item.monthlyExpense, item.amountFrequency) * months);
            break;

        case ITEM_TYPES.LOAN:
            push('debtPayment', -debtPaymentDue(item, to) * months);
            break;

        default:
            break;
    }

    return parts;
};

const sum = (entries) => entries.reduce((total, entry) => total + entry.value, 0);

// A row whose value rounds to nothing is dropped: it would only invite the
// question of why a "+0" is being shown. Single-part rows carry no breakdown —
// repeating the same number underneath itself explains nothing.
const significant = (entry) => Math.round(entry.value) !== 0;

const side = (rows) => ({
    total: sum(rows),
    items: rows
        .filter(significant)
        .map(row => {
            // Filter first, then decide: one meaningful part restates the row's
            // own figure, so it isn't a breakdown at all.
            const parts = row.parts.filter(significant);
            return { ...row, parts: parts.length > 1 ? parts : [] };
        })
        .sort((a, b) => Math.abs(b.value) - Math.abs(a.value)),
});

/**
 * What the portfolio did between `lastSeenAt` and `now`, as two sides, each
 * with the items behind it and each item with its parts. Null when there is no
 * window to measure.
 *
 * `currentNetWorth` is passed in rather than derived so this module stays clear
 * of analytics.js: the caller sends `calculateAllAnalytics(...).netWorth +
 * liquidAssets`, which is what the dashboard displays, so the percentage here
 * and the figure up there can never disagree.
 *
 * -> {
 *      months, days,
 *      assets:   { total, items: [{ id, name, value, parts: [{ key, value }] }] },
 *      cashflow: { total, items: [...] },   // includes a synthetic savings-interest row
 *      total, pct, currentNetWorth,
 *    }
 */
export const computeAwayDelta = ({
    items = [],
    liquidAssets = 0,
    liquidAssetsInterestRate = 0,
    currentNetWorth = 0,
    lastSeenAt,
    now,
}) => {
    const from = toDate(lastSeenAt);
    const to = toDate(now);
    if (!from || !to) return null;

    const months = monthsBetween(from, to);
    if (months <= 0) return null; // zero, negative, or clock skew

    const assetRows = [];
    const flowRows = [];

    items.forEach((item) => {
        const assetParts = assetPartsOf(item, from, to, months);
        if (assetParts.length) {
            assetRows.push({ id: item.id, name: item.name, value: sum(assetParts), parts: assetParts });
        }

        const flowParts = flowPartsOf(item, to, months);
        if (flowParts.length) {
            flowRows.push({ id: item.id, name: item.name, value: sum(flowParts), parts: flowParts });
        }
    });

    // Cash uses the portfolio's real monthly cashflow, instalments included —
    // the same figure the dashboard shows. The instalment is not netted off
    // here, because the principal inside it reappears on the asset side.
    const monthlyNetCashflow = items.reduce(
        (total, item) => total + itemMonthlyCashflow(item, { asOf: to }),
        0,
    );
    const cashNow = num(liquidAssets);
    const cashLater = estimateLiquidAssets(cashNow, liquidAssetsInterestRate, monthlyNetCashflow, from, to);
    const cashTotal = usable(cashLater) ? cashLater - cashNow : 0;

    // Whatever the item flows don't account for is the interest the balance
    // earned, including interest on money that arrived during the window.
    // Derived as the remainder, so the rows always sum to the total exactly.
    flowRows.push({
        id: 'liquid',
        name: null,
        value: cashTotal - sum(flowRows),
        parts: [],
    });

    const assets = side(assetRows);
    const cashflow = side(flowRows);

    const total = assets.total + cashflow.total;
    const netWorth = num(currentNetWorth);

    return {
        months,
        days: daysBetween(from, to),
        assets,
        cashflow,
        total,
        pct: netWorth > 0 ? total / netWorth : 0,
        currentNetWorth: netWorth,
    };
};

/**
 * Whether a computed delta is worth interrupting the dashboard for.
 *
 * Kept separate from the maths so the same numbers can feed something with
 * different manners later — the monthly "your plan moved" email, say.
 */
export const shouldShowAwayDelta = (delta) => {
    if (!delta) return false;
    if (delta.currentNetWorth <= 0) return false; // empty portfolio, new account
    if (delta.days < MIN_AWAY_DAYS) return false;
    if (Math.abs(delta.total) < MIN_DELTA_ABS) return false;
    return Math.abs(delta.pct) >= MIN_DELTA_PCT;
};
